import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { useMemo, useRef } from "react";
import { useMarkReadConversationAction } from "../actions/use-conversation-actions";
import { useGlobalConversationCollections } from "../providers/global-conversation-collections-context";

/**
 * Hook that automatically marks a conversation as read when it's first viewed.
 * Uses useMemo to ensure the side effect only runs once per conversation.
 * Tracks the last processed conversation ID to handle navigation between conversations.
 */
export function useMarkConversationAsRead(
  conversationId: ConversationId,
): void {
  const markReadAction = useMarkReadConversationAction();
  const lastConversationIdRef = useRef<ConversationId | null>(null);
  const collections = useGlobalConversationCollections();
  const conversation = useLiveQuery(
    (q) =>
      q
        .from({ c: collections.conversationsList.collection })
        .where(({ c }) => eq(c.id, conversationId))
        .findOne(),
    [conversationId],
  ).data;

  useMemo(() => {
    if (!conversation || lastConversationIdRef.current === conversationId) {
      return;
    }

    // Mark as read if:
    // 1. readAt is null (never read), OR
    // 2. A customer message arrived after the last read time
    const isUnread =
      conversation.readAt === null ||
      (conversation.lastCustomerMessageAt !== null &&
        conversation.lastCustomerMessageAt > conversation.readAt);

    if (isUnread) {
      void markReadAction.execute(conversationId);
      lastConversationIdRef.current = conversationId;
    }
  }, [conversation, conversationId, markReadAction]);
}
